import React, { useEffect, useMemo, useState } from 'react';
import { Modal, message, Button, Form, Table } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useI18n } from '../i18n/I18nContext';
import { getInvoices, Invoice, getLedgerEntries } from '../firebase/firestore';
import CreateInvoiceForm from '../components/CreateInvoiceForm';
import { DeliveryFields, offerLabels } from '../components/InvoiceOfferFields';
import api from '../api/api';
import {
  NotionTable,
  NotionPill,
  NotionPerson,
  NotionIcons,
  type NotionColumn,
  type NotionPillColor,
} from '../components/notion/NotionTable';
import { useNotionMessages } from '../components/notion/useNotionMessages';
import './Invoices.css';

type ViewKey = 'all' | 'paid' | 'sent' | 'created' | 'expired';

const Invoices: React.FC = () => {
  const { firebaseUser, user } = useAuth();
  const { includesInvoice } = useProjects();
  const { t } = useI18n();
  const { table: tt, invoicesTable: ti, locale } = useNotionMessages();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewKey>('all');
  const [paymentCounts, setPaymentCounts] = useState<Record<string, number>>({});
  const [copiedInvoiceId, setCopiedInvoiceId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalSuccess, setCreateModalSuccess] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [deliveryActivity, setDeliveryActivity] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityVersion, setActivityVersion] = useState(0);
  const [publishInvoice, setPublishInvoice] = useState<Invoice | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishForm] = Form.useForm();
  const [detail, setDetail] = useState<Invoice | null>(null);

  useEffect(() => {
    if (!firebaseUser) return;

    const loadInvoices = async () => {
      try {
        setLoading(true);
        const data = await getInvoices(firebaseUser.uid, undefined, 200);
        setInvoices(data);
        setError(null);
      } catch (err: any) {
        setError(err.message || t.app.invoices.failedLoad);
      } finally {
        setLoading(false);
      }
    };

    const loadPaymentCounts = async () => {
      try {
        const ledgerEntries = await getLedgerEntries(firebaseUser.uid, undefined, 1000);
        const counts: Record<string, number> = {};
        if (Array.isArray(ledgerEntries)) {
          ledgerEntries.forEach((entry) => {
            if (entry?.invoiceId && entry?.type === 'GROSS') {
              counts[entry.invoiceId] = (counts[entry.invoiceId] || 0) + 1;
            }
          });
        }
        setPaymentCounts(counts);
      } catch (err) {
        console.error('Failed to load payment counts:', err);
      }
    };

    loadInvoices();
    loadPaymentCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser, reloadKey]);

  useEffect(() => {
    let canceled = false;
    setDeliveryActivity([]);
    if (!detail) return;
    setActivityLoading(true);
    api.get(`/invoices/${detail.id}/activity`).then(response => {
      if (!canceled) setDeliveryActivity(response.data);
    }).catch(() => { if (!canceled) message.error(locale === 'fr' ? 'Impossible de charger les livraisons' : 'Could not load deliveries'); })
      .finally(() => { if (!canceled) setActivityLoading(false); });
    return () => { canceled = true; };
  }, [detail, activityVersion, locale]);

  /* ---------- Vues ---------- */
  const matchesView = (invoice: Invoice, view: ViewKey) => {
    switch (view) {
      case 'paid': return invoice.status === 'PAID';
      case 'sent': return invoice.status === 'SENT';
      case 'created': return invoice.status === 'CREATED';
      case 'expired': return invoice.status === 'EXPIRED' || invoice.status === 'CANCELED';
      default: return true;
    }
  };

  const visibleInvoices = useMemo(
    () => invoices.filter((invoice) => includesInvoice(invoice.id)),
    [invoices, includesInvoice],
  );

  const rows = useMemo(() => visibleInvoices.filter((i) => matchesView(i, activeView)), [visibleInvoices, activeView]);

  const views = useMemo(
    () => [
      { key: 'all', label: tt.viewTable, count: visibleInvoices.length },
      { key: 'paid', label: t.app.invoices.paid, count: visibleInvoices.filter((i) => matchesView(i, 'paid')).length },
      { key: 'sent', label: t.app.invoices.sent, count: visibleInvoices.filter((i) => matchesView(i, 'sent')).length },
      { key: 'created', label: t.app.invoices.created, count: visibleInvoices.filter((i) => matchesView(i, 'created')).length },
      { key: 'expired', label: t.app.invoices.expired, count: visibleInvoices.filter((i) => matchesView(i, 'expired')).length },
    ],
    [visibleInvoices, t, tt],
  );

  /* ---------- Helpers ---------- */
  const formatDate = (date: Date) =>
    date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });

  const statusMeta = (status: Invoice['status']): { label: string; color: NotionPillColor } => {
    switch (status) {
      case 'PAID': return { label: t.app.invoices.statusPaid, color: 'green' };
      case 'SENT': return { label: t.app.invoices.statusSent, color: 'blue' };
      case 'CREATED': return { label: t.app.invoices.statusCreated, color: 'yellow' };
      case 'EXPIRED':
      case 'CANCELED': return { label: t.app.invoices.statusExpired, color: 'red' };
      default: return { label: String(status).toLowerCase(), color: 'gray' };
    }
  };

  const getUserName = () => firebaseUser?.displayName || user?.email?.split('@')[0] || t.app.common.you;

  const invoiceTitle = (invoice: Invoice) =>
    invoice.title || t.app.dashboard.invoiceLabel.replace('{id}', invoice.id.slice(0, 8));

  const handleCopyLink = async (invoice: Invoice) => {
    if (!invoice.telegramInvoiceLink) {
      message.error(t.app.invoices.linkNotAvailable);
      return;
    }
    try {
      await navigator.clipboard.writeText(invoice.telegramInvoiceLink);
      setCopiedInvoiceId(invoice.id);
      message.success(t.app.invoices.linkCopied);
      setTimeout(() => setCopiedInvoiceId(null), 2000);
    } catch {
      message.error(t.app.invoices.copyFailed);
    }
  };

  /* ---------- Colonnes ---------- */
  const columns: NotionColumn<Invoice>[] = useMemo(
    () => [
      {
        key: 'title',
        label: ti.colTitle,
        type: 'title',
        width: 'minmax(240px, 1.6fr)',
        accessor: (r) => invoiceTitle(r),
      },
      {
        key: 'status',
        label: ti.colStatus,
        type: 'select',
        width: 130,
        accessor: (r) => statusMeta(r.status).label,
        render: (r) => {
          const m = statusMeta(r.status);
          return <NotionPill color={m.color}>{m.label}</NotionPill>;
        },
      },
      {
        key: 'type',
        label: ti.colType,
        type: 'multi',
        width: 150,
        accessor: (r) => offerLabels(locale === 'fr')[r.invoiceType || (r.singleUse ? 'SINGLE' : 'MULTIPLE')],
        render: (r) => (
          <>
            <NotionPill color={r.singleUse ? 'orange' : 'purple'}>
              {offerLabels(locale === 'fr')[r.invoiceType || (r.singleUse ? 'SINGLE' : 'MULTIPLE')]}
            </NotionPill>
            <NotionPill color="gray">{r.inputMode}</NotionPill>
          </>
        ),
      },
      {
        key: 'amount',
        label: ti.colAmount,
        type: 'number',
        width: 130,
        align: 'right',
        accessor: (r) => r.starsAmount,
        render: (r) => (
          <span className="nt-num">
            {r.starsAmount.toLocaleString(locale)} <span style={{ color: '#9b9a97' }}>★</span>
          </span>
        ),
      },
      {
        key: 'payments',
        label: ti.colPayments,
        type: 'number',
        width: 110,
        align: 'right',
        accessor: (r) => paymentCounts[r.id] || 0,
      },
      {
        key: 'description',
        label: ti.colDescription,
        type: 'text',
        width: 'minmax(180px, 1.2fr)',
        accessor: (r) => r.description || '',
      },
      {
        key: 'created',
        label: ti.colCreated,
        type: 'date',
        width: 140,
        accessor: (r) => r.createdAt,
        render: (r) => formatDate(r.createdAt),
      },
      {
        key: 'owner',
        label: t.app.invoices.relatedTo.replace(':', '').trim(),
        type: 'person',
        width: 170,
        sortable: false,
        accessor: () => getUserName(),
        render: () => <NotionPerson name={getUserName()} />,
      },
      {
        key: 'link',
        label: ti.colLink,
        type: 'url',
        width: 150,
        sortable: false,
        accessor: (r) => r.telegramInvoiceLink || '',
        render: (r) =>
          r.telegramInvoiceLink ? (
            <button
              type="button"
              className={`nt-inline-btn ${copiedInvoiceId === r.id ? 'done' : ''}`}
              onClick={() => handleCopyLink(r)}
              title={t.app.invoices.copyLinkTitle}
            >
              {copiedInvoiceId === r.id ? <CheckOutlined /> : <CopyOutlined />}
              {copiedInvoiceId === r.id ? t.app.common.copied : t.app.invoices.copyLink}
            </button>
          ) : (
            <span className="nt-empty-cell" />
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ti, t, locale, paymentCounts, copiedInvoiceId, firebaseUser, user],
  );

  if (error) {
    return <div className="invoices-error">{t.app.common.error}: {error}</div>;
  }

  const detailStatus = detail ? statusMeta(detail.status) : null;

  return (
    <div className="invoices-notion">

      <NotionTable<Invoice>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        views={views}
        activeView={activeView}
        onViewChange={(k) => setActiveView(k as ViewKey)}
        newLabel={t.app.invoices.createInvoice.replace(/^\+\s*/, '')}
        onNew={() => setCreateModalOpen(true)}
        onOpen={(r) => setDetail(r)}
        defaultSort={{ key: 'created', dir: 'desc' }}
        loading={loading}
        formatDate={formatDate}
        labels={{
          search: tt.search,
          sort: tt.sort,
          filter: tt.filter,
          open: tt.open,
          count: tt.count,
          empty: t.app.invoices.noInvoices,
          clearSort: tt.clearSort,
          ascending: tt.ascending,
          descending: tt.descending,
        }}
      />

      <Modal open={!!publishInvoice} title={locale === 'fr' ? 'Publier aux abonnés actifs' : 'Publish to active subscribers'} footer={null} onCancel={() => { setPublishInvoice(null); publishForm.resetFields(); }} destroyOnClose>
        <Form form={publishForm} layout="vertical" onFinish={async values => {
          if (!publishInvoice) return;
          setPublishing(true);
          try {
            const response = await api.post(`/invoices/${publishInvoice.id}/publish`, { type: values.deliveryType, value: values.deliveryValue, message: values.deliveryMessage });
            message.success(locale === 'fr' ? `${response.data.queued} livraison(s) programmée(s)` : `${response.data.queued} delivery(s) queued`);
            setPublishInvoice(null); publishForm.resetFields();
          } catch (error: any) { message.error(error.response?.data?.message || 'Publication failed'); }
          finally { setPublishing(false); }
        }}>
          <DeliveryFields />
          <Button htmlType="submit" type="primary" loading={publishing}>{locale === 'fr' ? 'Envoyer aux abonnés actifs' : 'Send to active subscribers'}</Button>
        </Form>
      </Modal>
      {/* Création */}
      <Modal
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        destroyOnClose
        width={560}
        className="create-invoice-modal"
        title={createModalSuccess ? t.app.createInvoice.successTitle : t.app.createInvoice.title}
      >
        <CreateInvoiceForm
          onCancel={() => setCreateModalOpen(false)}
          onCreated={() => setReloadKey((k) => k + 1)}
          onViewInvoices={() => setCreateModalOpen(false)}
          onSuccessViewChange={setCreateModalSuccess}
        />
      </Modal>

      {/* Détail "page" façon Notion */}
      <Modal open={!!detail} onCancel={() => setDetail(null)} footer={null} width={620} destroyOnClose className="nt-detail-modal">
        {detail && detailStatus && (
          <div className="nt-detail">
            <div className="nt-detail-title">
              <span className="nt-page-icon">{NotionIcons.page}</span>
              {invoiceTitle(detail)}
            </div>
            {detail.invoiceType === 'SUBSCRIPTION' && <div style={{ marginBottom: 16 }}>
              <p>{detail.subscription?.mode === 'AUTO' ? (locale === 'fr' ? 'Renouvellement automatique tous les 30 jours' : 'Automatic renewal every 30 days') : `${detail.subscription?.periodDays || 30} ${locale === 'fr' ? 'jours · renouvellement manuel' : 'days · manual renewal'}`}</p>
              <Button onClick={() => { setPublishInvoice(detail); setDetail(null); }}>{locale === 'fr' ? 'Publier aux abonnés actifs' : 'Publish to active subscribers'}</Button>
            </div>}
            <div className="nt-props">
              <div className="nt-prop-label">{NotionIcons.select} {ti.colStatus}</div>
              <div className="nt-prop-value"><NotionPill color={detailStatus.color}>{detailStatus.label}</NotionPill></div>

              <div className="nt-prop-label">{NotionIcons.multi} {ti.colType}</div>
              <div className="nt-prop-value">
                <NotionPill color={detail.singleUse ? 'orange' : 'purple'}>
                  {offerLabels(locale === 'fr')[detail.invoiceType || (detail.singleUse ? 'SINGLE' : 'MULTIPLE')]}
                </NotionPill>
                <NotionPill color="gray">{detail.inputMode}</NotionPill>
              </div>

              <div className="nt-prop-label">{NotionIcons.number} {ti.colAmount}</div>
              <div className="nt-prop-value">
                {detail.starsAmount.toLocaleString(locale)} ★
                {detail.inputMode !== 'STARS' && (
                  <span style={{ color: '#787774' }}>
                    · {detail.inputAmount} {detail.inputMode}
                  </span>
                )}
              </div>

              <div className="nt-prop-label">{NotionIcons.number} {ti.colPayments}</div>
              <div className="nt-prop-value">{paymentCounts[detail.id] || 0}</div>

              <div className="nt-prop-label">{NotionIcons.date} {ti.colCreated}</div>
              <div className="nt-prop-value">{formatDate(detail.createdAt)}</div>

              {detail.paidAt && (
                <>
                  <div className="nt-prop-label">{NotionIcons.date} {t.app.invoices.statusPaid}</div>
                  <div className="nt-prop-value">{formatDate(detail.paidAt)}</div>
                </>
              )}

              <div className="nt-prop-label">{NotionIcons.text} {ti.colDescription}</div>
              <div className="nt-prop-value">{detail.description || <span style={{ color: '#9b9a97' }}>—</span>}</div>

              <div className="nt-prop-label">{NotionIcons.url} {ti.colLink}</div>
              <div className="nt-prop-value" style={{ gap: 8, flexWrap: 'wrap' }}>
                {detail.telegramInvoiceLink ? (
                  <>
                    <button
                      type="button"
                      className={`nt-inline-btn ${copiedInvoiceId === detail.id ? 'done' : ''}`}
                      onClick={() => handleCopyLink(detail)}
                    >
                      {copiedInvoiceId === detail.id ? <CheckOutlined /> : <CopyOutlined />}
                      {copiedInvoiceId === detail.id ? t.app.common.copied : t.app.invoices.copyLink}
                    </button>
                    <a
                      className="nt-inline-btn"
                      href={detail.telegramInvoiceLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {NotionIcons.open} {ti.openLink}
                    </a>
                  </>
                ) : (
                  <span style={{ color: '#9b9a97' }}>{t.app.invoices.linkNotAvailable}</span>
                )}
              </div>
            </div>
            <h4>{locale === 'fr' ? 'Paiements et livraisons récents' : 'Recent payments and deliveries'}</h4>
            <Table size="small" rowKey="id" dataSource={deliveryActivity} loading={activityLoading} pagination={{ pageSize: 5 }} columns={[
              { title: locale === 'fr' ? 'Client Telegram' : 'Telegram buyer', dataIndex: 'telegramUserId' },
              { title: 'Stars', dataIndex: 'starsAmount', render: (amount: number | null) => amount ?? '—' },
              { title: locale === 'fr' ? 'Livraison' : 'Delivery', dataIndex: 'deliveryStatus', render: (status: string) => ({ NONE: '—', PENDING: locale === 'fr' ? 'En attente' : 'Pending', PROCESSING: locale === 'fr' ? 'En cours' : 'Processing', SENT: locale === 'fr' ? 'Envoyé' : 'Sent', FAILED: locale === 'fr' ? 'Échec' : 'Failed', EXPIRED: locale === 'fr' ? 'Expiré' : 'Expired' }[status] || status) },
              { title: '', render: (_: unknown, row: any) => row.deliveryStatus === 'FAILED' ? <Button size="small" onClick={async () => {
                try { await api.post(`/invoices/deliveries/${row.id}/retry`); setActivityVersion(v => v + 1); }
                catch (error: any) { message.error(error.response?.data?.message || 'Retry failed'); }
              }}>{locale === 'fr' ? 'Réessayer' : 'Retry'}</Button> : null },
            ]} />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Invoices;
